/* eslint-disable no-nested-ternary */
import React, { useEffect, useId, useState } from 'react';

import '@js-joda/core';
import {
  useCreateProductTemplateEntry,
  useDeleteProductTemplateEntry,
  useGetProductTemplateConfig,
  useGetProductTemplateData,
  useGetProductTemplateFilterPreviewData,
  useSignProductTemplateEntry,
  useUpdateProductTemplateEntry,
} from 'api/api-hooks/product-templates-hooks';
import ExportDataTableButton from 'components/basic/export-data-table/ExportDataTable';
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import { useComponentStateCallback } from 'hooks/dashboard/useComponentStateCallback';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { CellType, SignStatus, TemplateLoadingType } from 'utils/enums';
import { addToDate, formatDateTimeOrNow } from 'utils/time-utils';
import CalendarWrapper from '../../basic/calendar-wrapper/CalendarWrapper';
import './ProductTemplateTable.scss';
import ProductTemplateTableDefault from './ProductTemplateTableDefault';
import ProductTemplateTableTransposed from './ProductTemplateTableTransposed';
import EditDataCell from './components/templates/table-components/edit-datacell';
import {
  IPeriodLoadingConfig,
  ITableDataEntry,
  ITableDataEntryValue,
  LoadingConfig,
  ProductTemplate,
  SignProductTemplateEntryResponse,
} from './utils/interfaces';
import { getTemplateLoadingRange } from './utils/timeUtils';
import ProductTemplateFilter from './components/ProductTemplateFilter';
import { DashboardComponentPropsBase, IComponentTimed } from 'components/pages/dashboard/components/interfaces';

export interface ReportOptions {
    splitAfter?: number;
    fontSize?: string;
}

export interface IProductTemplateTableProps extends
  DashboardComponentPropsBase<ReportOptions>,
  IComponentTimed,
  ProductTemplate.IHorizontalCommonConfig
{
    safeName: string;
    token?: string;
    customHost?: string;
    transposed?: boolean;
    showTitle?: boolean;
    showTotals?: boolean;
    updateFrequency?: number;
    hiddenColumns?: Array<string>;
    onlyShownColumns?: Array<string>;
    itemsPerPage?: number;
    overrideLoadingConfig?: LoadingConfig;
    filter?: string;
    defaultFilter?: string;
    onFetchError?: (val: string) => void;
    retry?: boolean | number;
    exactDateFrom?: Date;
    exactDateTo?: Date;
    hideDates?: boolean;
    showFilter?: boolean;
    useExternalFilterEndpoint?: boolean;
    allowAddEntry?: boolean;
    allowRowEdit?: boolean;
    foreignKeyWrap?: boolean;
}

export default function ProductTemplateTable(props: IProductTemplateTableProps) {
    const id = useId();
    const setStatus = useComponentStateCallback(props);
    const [filter, setFilter] = useState<string | undefined>(props.filter);

    const allowRowEdit = props.allowRowEdit ?? true;
    const transposed = !!props.transposed;
    const showTitle = props.showTitle ?? true;

    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [datesChanged, setDatesChanged] = useState<boolean>(false);

    const [allowAddEntry, setAllowAddEntry] = useState<boolean>(allowRowEdit);
    const [allowDelete, setAllowDelete] = useState<boolean>(true);

    const [disabledDatesFrom, setDisabledDatesFrom] = useState<Date[] | undefined>([]);
    const [disabledDatesTo, setDisabledDatesTo] = useState<Date[] | undefined>([]);

    const [selectedEntryIndex, setSelectedEntryIndex] = useState<number | undefined>(undefined);

    const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>('');
    const [selectedEntryBody, setSelectedEntryBody] = useState<ITableDataEntry | undefined>(undefined);

    const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);

    const [page, setPage] = useState(0);

    const [updateFrequency, setUpdateFrequency] = useState<number | boolean>(
        props.updateFrequency && (props.reportMode === undefined || !props.reportMode)
            ? props.updateFrequency * 1000
            : false
    );
    const [fetchData, setFetchData] = useState(true);
    const [displayedTemplateData, setDisplayedTemplateData] = useState<ITableDataEntry[]>([]);

    const getProductTemplateDataSelectedFunction = props.useExternalFilterEndpoint
        ? useGetProductTemplateFilterPreviewData
        : useGetProductTemplateData;

    const { mutate: createNewEntry, status: createNewEntryStatus } = useCreateProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Created');
            },
            onFailure: () => {
                notifyError('Failed to create entry!');
            },
        }
    );
    const { mutate: updateNewEntry } = useUpdateProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Updated');
            },
            onFailure: () => {
                notifyError('Failed to update entry!');
            },
        }
    );
    const { mutate: deleteEntry } = useDeleteProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            filter,
        },
        {
            onSuccess: () => {
                notifySuccess('Deleted');
            },
            onFailure: () => {
                notifyError('Failed to delete entry!');
            },
        }
    );

    const { mutate: signEntryData, data: signData } = useSignProductTemplateEntry(
        {
            safeName: props.safeName,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            filter,
        },
        {
            onSuccess: (data) => {
                if (data.data.signStatus === SignStatus.CONDITIONS_NOT_MET) {
                  setErrorDialogOpen(true);
                } else {
                 notifySuccess('Updated');
                }
            },
            onFailure: () => {
                notifyError('Failed to update entry!');
            },
        }
    );

    const {
        data: configData,
        isError: configError,
        isLoading: isLoadingConfig,
    } = useGetProductTemplateConfig(props.safeName, id, {
        usePromiseHandler: (res: any) => {
            if (res.error) {
                console.log(res.error.message);
                throw res.error;
            }

            const { data } = res;

            if (props.hiddenColumns && props.hiddenColumns.length > 0) {
                const attributes = data.data.attributes.filter((attribute: any) => {
                    return !props.hiddenColumns?.includes(attribute.referenceName);
                });

                return {
                    ...data,
                    data: {
                        ...data.data,
                        attributes,
                    },
                };
            } if (props.onlyShownColumns && props.onlyShownColumns.length > 0) {
                const attributes = data.data.attributes.filter((attribute: any) => {
                    return props.onlyShownColumns?.includes(attribute.referenceName);
                });

                return {
                    ...data,
                    data: {
                        ...data.data,
                        attributes,
                    },
                };
            }

            return data;
        },
    });

    const {
        data: templateData,
        isError: templateError,
        isLoading: isLoadingTemplate,
        isFetching: isTemplateDataFetching} = getProductTemplateDataSelectedFunction(
        {
            safeName: props.safeName,
            dateFrom: dateFrom?.toISOString(),
            dateTo: dateTo?.toISOString(),
            filter,
        },
        {
            enabled: !!configData && !!dateFrom && !!dateTo && fetchData,
            refetchInterval: updateFrequency,
            retry: false,
            staleTime: 0,
            onError: (er) => {
                if (props.onFetchError) {
                    props.onFetchError(er.message);
                }
            },
        }
    );

    const handleButtonClick = () => {
        setFetchData(true);
        setDatesChanged(true);
        setUpdateFrequency(false);
    };

    useEffect(() => {
        if (templateData) {
            setDisplayedTemplateData(templateData);
        }
    }, [fetchData, templateData]);

    useEffect(() => {
        if (!isLoadingConfig && configData) {
            setAllowAddEntry(
                props.allowAddEntry !== undefined
                    ? props.allowAddEntry
                    : configData?.data?.config?.allowRowAddition != null
                    ? configData.data.config.allowRowAddition
                    : true
            );
            setAllowDelete(
                configData.data?.config?.allowRowDeletion != null ? configData?.data?.config?.allowRowDeletion : true
            );
            setDatesByConfigAndLoadingType();
        }
    }, [configData]);

    useEffect(() => {
        if (dateFrom) {
            disableDateDays(dateFrom, -1, setDisabledDatesTo);
        }
        if (dateTo) {
            disableDateDays(dateTo, 1, setDisabledDatesFrom);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        if (props.exactDateFrom) {
            setDateFrom(props.exactDateFrom);
        }
        if (props.exactDateTo) {
            setDateTo(props.exactDateTo);
        }
    }, [props.exactDateFrom, props.exactDateTo]);

    const setDatesByConfigAndLoadingType = (wasClicked = false) => {
        if (configData && !props.exactDateFrom && !props.exactDateTo) {
            const loadingType: TemplateLoadingType = props.overrideLoadingConfig?.loadingType
                ? props.overrideLoadingConfig.loadingType
                : configData?.data?.config?.loadingType!;
            const loadingConfig: IPeriodLoadingConfig = {
                ...configData?.data?.config?.loadingConfig,
                ...props.overrideLoadingConfig?.loadingConfig,
            };

            const { start, end } = getTemplateLoadingRange(
                {
                    loadingType,
                    loadingConfig,
                },
                props.referenceTime
            );

            setDateFrom(start);
            setDateTo(end);
            if (wasClicked && fetchData) {
                setFetchData(prevState => !prevState);
            }
            setDatesChanged(false);
        }
    };

    const disableDateDays = (initDate: Date, step: number, setter: CallableFunction) => {
        const date = new Date(initDate);
        const year = date.getFullYear();
        const allDays = [];
        while (date.getFullYear() === year - 1 || date.getFullYear() === year || date.getFullYear() === year + 1) {
            const d =
                `${date.getFullYear() 
                }-${ 
                (date.getMonth() + 1).toString().padStart(2, '0') 
                }-${ 
                date.getDate().toString().padStart(2, '0')}`;
            allDays.push(new Date(d));
            date.setDate(date.getDate() + step);
        }
        setter(allDays);
    };

    const editedDataHandler = (indexEntry: number, refName: string, newValue: any, newCell?: ITableDataEntryValue) => {
        if (selectedEntryId && selectedEntryBody) {
            const tempEntry = { ...selectedEntryBody };

            if (tempEntry.entries[refName] != null) {
                tempEntry.entries[refName].value = newValue;
            } else if (newCell) {
                    tempEntry.entries[newCell.referenceName] = newCell;
                }
            setSelectedEntryBody(tempEntry);
        }
    };

    const manageRowEdit = (entryIndex: number, editable: boolean) => {
        if (editable && displayedTemplateData && displayedTemplateData.length >= entryIndex) {
            const offsetedIndex = entryIndex + page;

            setSelectedEntryBody(JSON.parse(JSON.stringify(displayedTemplateData[offsetedIndex])));
            setSelectedEntryId(displayedTemplateData[offsetedIndex].id);
            setSelectedEntryIndex(entryIndex);
            setUpdateFrequency(false);
        } else {
            if (!datesChanged) {
                setUpdateFrequency(
                    props.updateFrequency && (props.reportMode === undefined || !props.reportMode)
                        ? props.updateFrequency * 1000
                        : false
                );
            }
            setSelectedEntryId(undefined);
            setSelectedEntryBody(undefined);
            setSelectedEntryIndex(undefined);
        }
    };

    const onClickEdit = (entryIndex: number) => {
        manageRowEdit(entryIndex, true);
    };

    const onClickCopy = (currentIndex: number) => {
        if (displayedTemplateData && displayedTemplateData.length > currentIndex) {
            const offsetedIndex = currentIndex + page;

            const entry = { ...displayedTemplateData[offsetedIndex] };
            let entryEntriesList = JSON.parse(JSON.stringify(Object.values(entry.entries)));

            entryEntriesList = entryEntriesList.filter((ob: any) => ob.type !== CellType.USER_SIGNATURE);

            createNewEntry({ body: entryEntriesList });
        }
    };

    const onClickSave = (entryIndex: number) => {
        if (selectedEntryId && selectedEntryBody) {
            updateNewEntry({ entryId: selectedEntryId, body: Object.values(selectedEntryBody.entries) });
        }
        manageRowEdit(entryIndex, false);
    };

    const onClickDelete = (entryIndex: number) => {
        const offsetedIndex = entryIndex + page;

        if (displayedTemplateData && displayedTemplateData[offsetedIndex].id != null) {
            deleteEntry({ entryId: displayedTemplateData[offsetedIndex].id!! });

            if (selectedEntryId != null || selectedEntryId !== '') {
                manageRowEdit(entryIndex, false);
            }
        } else {
            notifyError('Entry index not found');
        }
    };

    const onClickCancel = (entryIndex: number) => {
        manageRowEdit(entryIndex, false);
    };

    const onClickSign = (entryIndex: number, referenceName: string) => {
        if (displayedTemplateData) {
            const offsetedIndex = entryIndex + page;

            const entryID = displayedTemplateData[offsetedIndex].id;
            if (entryID != null) {
                signEntryData({ entryId: entryID, referenceName });
            }
        }
    };

    const onClickAddEntry = () => {
        let body: Array<any> = [];
        const dateString = formatDateTimeOrNow('yyyy-MM-dd HH:mm');

        if (configData) {
            const val = configData?.data?.attributes.find((ob: any) => ob.referenceName === configData.keyColumn);

            if (val) {
                if (val.type === 'DATETIME') {
                    body = [
                        {
                            referenceName: val.referenceName,
                            type: val.type,
                            value: dateString,
                        },
                    ];
                }
                return createNewEntry({ body });
            }
            easasAlert({
              summary: "Failed to create new entry",
              detail: `Column with key "${configData.keyColumn}" not found`,
              severity: "error",
            });
        }
    };
    
    const notifySuccess = (msg: string) => {
        easasAlert({ summary: "Success", detail: msg, severity: "success" });
    };
    
    const notifyError = (msg: string) => {
        easasAlert({ summary: "Error", detail: msg, severity: "error" });
    };

    function EditRowDataCell(val: any, index: number) {
        let disableEdit = '';

        if (
            configData &&
            configData.data?.config?.signatureAttribute &&
            val.entries[configData.data.config.signatureAttribute] &&
            val.entries[configData.data.config.signatureAttribute].value.signed
        ) {
            disableEdit = val.entries[configData.data.config.signatureAttribute].value.display;
        }

        return (
            <EditDataCell
                key={val.id}
                index={index}
                onClickCopy={onClickCopy}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickSave={onClickSave}
                onClickCancel={onClickCancel}
                isInEditMode={selectedEntryId === val.id}
                deletable={allowDelete}
                signedBy={disableEdit}
            />
        );
    }

    const handleKeyEnterPress = () => {
        if (selectedEntryIndex != null) {
            onClickSave(selectedEntryIndex);
        }
    };

    const handleTableKeyDown = (key: string) => {
        if (key === 'Enter' && selectedEntryId) {
            handleKeyEnterPress();
        }
    };

    const handleFixedPeriodChange = (e: any) => {
        setDateFrom(e);
        if (configData?.data?.config && configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD) {
            const tempLoadingConf: IPeriodLoadingConfig = {
                ...configData.data.config.loadingConfig,
                ...props.overrideLoadingConfig?.loadingConfig,
            };
            const newDateTo = addToDate(e, tempLoadingConf.period as number, tempLoadingConf.periodUnit);
            setDateTo(newDateTo);
        }
    };

    const handleFilterApply = (filterLine: string) => {
      setFilter(props.filter ? `${props.filter}${filterLine ? `;${filterLine}` : ""}` : filterLine);
      setFetchData(true);
    }

    const DateToConfigInitializeButton = () => {
        if (datesChanged) {
            return (
                <div>
                    <Button
                        icon="pi pi-history"
                        style={{ transform: 'scale(95%)' }}
                        onClick={() => {
                            setDatesChanged(false);
                            setDatesByConfigAndLoadingType(true);
                            setFetchData(true);
                            setUpdateFrequency(
                                props.updateFrequency && (props.reportMode === undefined || !props.reportMode)
                                    ? props.updateFrequency * 1000
                                    : false
                            );
                        }}
                        title="Return default dates"
                        disabled={selectedEntryId !== '' && selectedEntryIndex != null}
                    />
                </div>
            );
        }
        return <></>;
    };

    const printSplitColumns = () => {
        if (props.reportOptions && props.reportOptions.splitAfter) {
            const data = [...(displayedTemplateData || [])];
            const resultArray = [];

            for (let i = 0; i < data.length; i += props.reportOptions.splitAfter) {
                const chunk = data.slice(i, i + props.reportOptions.splitAfter);
                resultArray.push(chunk);
            }

            return resultArray.map((smallChunkArray) => {
                return (
                    <ProductTemplateTableTransposed
                        configData={configData}
                        templateData={smallChunkArray}
                        editedDataHandler={editedDataHandler}
                        selectedEntryBody={selectedEntryBody}
                        selectedEntryIndex={selectedEntryIndex}
                        selectedEntryId={selectedEntryId}
                        onClickSign={onClickSign}
                        handleTableKeyDown={handleTableKeyDown}
                        itemsPerPage={props.itemsPerPage}
                        page={page}
                        setPage={setPage}
                        EditRowDataCell={EditRowDataCell}
                        reportOptions={props.reportOptions}
                        foreignKeyWrap={props.foreignKeyWrap}
                    />
                );
            });
        }

        return <>Number of columns to break is not defined</>;
    };

    const printSplitRows = () => {
        if (props.reportOptions && props.reportOptions.splitAfter) {
            const data = [...(displayedTemplateData || [])];
            const resultArray = [];

            for (let i = 0; i < data.length; i += props.reportOptions.splitAfter) {
                const chunk = data.slice(i, i + props.reportOptions.splitAfter);
                resultArray.push(chunk);
            }

            return resultArray.map((smallChunkArray) => {
                return (
                    <ProductTemplateTableDefault
                        configData={configData}
                        templateData={smallChunkArray}
                        editedDataHandler={editedDataHandler}
                        selectedEntryBody={selectedEntryBody}
                        selectedEntryIndex={selectedEntryIndex}
                        selectedEntryId={selectedEntryId}
                        onClickSign={onClickSign}
                        page={page}
                        setPage={setPage}
                        handleTableKeyDown={handleTableKeyDown}
                        EditRowDataCell={EditRowDataCell}
                        itemsPerPage={props.itemsPerPage}
                        reportOptions={props.reportOptions}
                        editColumnLocation={props.editColumnLocation}
                        allowRowEdit={allowRowEdit}
                        foreignKeyWrap={props.foreignKeyWrap}
                        showTotals={props.showTotals}
                    />
                );
            });
        }

        return <>Number of columns to break is not defined</>;
    };

    const calendarMinuteStep = 5;

    useEffect(() => {
        if (configError || templateError) {
            setStatus({ state: 'error', error: 'No data found' });
        } else if (!configData) {
            if (!isLoadingConfig && !isLoadingTemplate) {
                setStatus({ state: 'error', error: 'Something went wrong' });
            }
        } else if (configData && templateData) {
            setStatus({ state: 'finished' });
        }
    }, [configError, templateError, configData, templateData, isLoadingConfig, isLoadingTemplate]);

    useEffect(() => {
        if (
            configData?.data?.config &&
            configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD &&
            dateFrom &&
            datesChanged
        ) {
            const tempLoadingConf: IPeriodLoadingConfig = {
                ...configData.data.config.loadingConfig,
                ...props.overrideLoadingConfig?.loadingConfig,
            };
            const newDateTo = addToDate(dateFrom, tempLoadingConf.period as number, tempLoadingConf.periodUnit);
            setDateTo(newDateTo);
        }
    }, [dateFrom]);

    const ErrorOnTemplateBody = () => {
        if (configError || templateError) {
            return (
                <div className='demo-container p-p-4'>
                    <Error text="No data found" />
                </div>
            );
        }
        return (
            <div className='demo-container p-p-4'>
                <ProgressSpinner className='p-d-block p-mx-auto' />
            </div>
        );
    };

    if (!configData) {
        if (!isLoadingConfig && !isLoadingTemplate) {
            return (
                <div>
                    <Error text="Something went wrong" />
                </div>
            );
        }
        return (
            <div>
                <div className='demo-container p-p-4'>
                    <ProgressSpinner className='p-d-block p-mx-auto' />
                </div>
            </div>
        );
    }

    const truncateStartUnit = props.overrideLoadingConfig?.loadingConfig?.truncateStartUnit || configData?.data?.config?.loadingConfig?.truncateStartUnit;

    const showTimeOnCalendar = truncateStartUnit === 'HOURS' || truncateStartUnit === 'MINUTES';

    const signResponse: SignProductTemplateEntryResponse | undefined = signData?.data;
    const uniqueColumns = new Set(signResponse?.validationErrors?.map(err => err.attributeDisplayName))

    return (
      <div className="product-template-full-window">
        <div className="product-template-wrapper">
          {showTitle && (
            <div className="title-window non-printable">{configData && configData.displayName}</div>
          )}

          <div className="product-template-header non-printable">
            {!props.hideDates && (
              <>
                {configData &&
                  configData?.data?.config &&
                  configData.data.config.loadingType === TemplateLoadingType.FIXED_PERIOD && (
                    <div className="calendars-and-button-wrapper">
                      <div className="calendars-wrapper">
                        Date:
                        <CalendarWrapper
                          id="time24"
                          value={dateFrom}
                          style={{ paddingLeft: "10px" }}
                          onChange={e => handleFixedPeriodChange(e)}
                          showTime={showTimeOnCalendar}
                          disabled={selectedEntryId !== "" && selectedEntryIndex != null}
                          dateFormat="yy-mm-dd"
                          stepMinute={calendarMinuteStep}
                        />
                      </div>
                      <Button
                        onClick={handleButtonClick}
                        icon="pi pi-play"
                        className="p-button-success"
                        title="Fetch Data"
                      />
                      <DateToConfigInitializeButton />
                    </div>
                  )}

                {configData &&
                  configData?.data?.config?.loadingType === TemplateLoadingType.RANGE && (
                    <div className="calendars-and-button-wrapper">
                      <div className="calendars-wrapper">
                        <div className="calendars-wrapper">
                          Date from
                          <CalendarWrapper
                            id="time24"
                            value={dateFrom}
                            style={{ paddingLeft: "10px" }}
                            onChange={e => {
                              setDateFrom(e);
                              setFetchData(false);
                            }}
                            showTime
                            dateFormat="yy-mm-dd"
                            disabled={selectedEntryId !== "" && selectedEntryIndex != null}
                            disabledDates={disabledDatesFrom}
                            stepMinute={calendarMinuteStep}
                          />
                        </div>
                        <div className="calendars-wrapper">
                          Date to
                          <CalendarWrapper
                            id="time24"
                            value={dateTo}
                            onChange={e => {
                              setDateTo(e);
                              setFetchData(false);
                            }}
                            showTime
                            dateFormat="yy-mm-dd"
                            disabled={selectedEntryId !== "" && selectedEntryIndex != null}
                            disabledDates={disabledDatesTo}
                            stepMinute={calendarMinuteStep}
                          />
                        </div>
                      </div>
                      <Button
                        onClick={handleButtonClick}
                        icon="pi pi-play"
                        className="p-button-success"
                        title="Fetch Data"
                        loading={isTemplateDataFetching}
                      />
                      <DateToConfigInitializeButton />
                    </div>
                  )}
              </>
            )}

            {/* Justification space between. PlaceHolder */}
            <div />

            {configData && (
              <div className="buttons-flex-wrapper">
                {props.showFilter && (
                  <ProductTemplateFilter
                    defaultFilter={props.defaultFilter}
                    onApplyFilter={handleFilterApply}
                    attributes={configData.data?.attributes ?? []}
                  />
                )}
                {allowAddEntry && (
                  <Button
                    label="Add entry"
                    className="p-button-sm p-button-success"
                    icon="pi pi-plus"
                    onClick={() => onClickAddEntry()}
                    disabled={
                      createNewEntryStatus === "pending" ||
                      (selectedEntryId !== "" && selectedEntryIndex != null)
                    }
                    loading={createNewEntryStatus === "pending"}
                  />
                )}
                <ExportDataTableButton
                  header={configData}
                  body={displayedTemplateData}
                  tableName={props.safeName}
                  transposed={transposed}
                  showTotal={props.showTotals}
                />
              </div>
            )}
          </div>

          {displayedTemplateData ? (
            displayedTemplateData.length > 0 ? (
              transposed ? (
                props.reportOptions?.splitAfter &&
                displayedTemplateData.length > props.reportOptions?.splitAfter ? (
                  printSplitColumns()
                ) : (
                  <ProductTemplateTableTransposed
                    configData={configData}
                    templateData={displayedTemplateData}
                    editedDataHandler={editedDataHandler}
                    selectedEntryBody={selectedEntryBody}
                    selectedEntryIndex={selectedEntryIndex}
                    selectedEntryId={selectedEntryId}
                    onClickSign={onClickSign}
                    handleTableKeyDown={handleTableKeyDown}
                    EditRowDataCell={EditRowDataCell}
                    itemsPerPage={props.itemsPerPage}
                    page={page}
                    setPage={setPage}
                    reportOptions={props.reportOptions}
                    foreignKeyWrap={props.foreignKeyWrap}
                    showTotals={props.showTotals}
                  />
                )
              ) : props.reportOptions?.splitAfter &&
                displayedTemplateData.length > props.reportOptions?.splitAfter ? (
                printSplitRows()
              ) : (
                <ProductTemplateTableDefault
                  configData={configData}
                  templateData={displayedTemplateData}
                  editedDataHandler={editedDataHandler}
                  selectedEntryBody={selectedEntryBody}
                  selectedEntryIndex={selectedEntryIndex}
                  selectedEntryId={selectedEntryId}
                  onClickSign={onClickSign}
                  handleTableKeyDown={handleTableKeyDown}
                  EditRowDataCell={EditRowDataCell}
                  page={page}
                  setPage={setPage}
                  itemsPerPage={props.itemsPerPage}
                  reportOptions={props.reportOptions}
                  editColumnLocation={props.editColumnLocation}
                  allowRowEdit={allowRowEdit}
                  foreignKeyWrap={props.foreignKeyWrap}
                  showTotals={props.showTotals}
                />
              )
            ) : isTemplateDataFetching || (templateData && templateData?.length > 0) ? (
              <ProgressSpinner className="overflow-hidden" />
            ) : (
              <div className="demo-container p-p-4">
                <Error text="No data" />
              </div>
            )
          ) : (
            <ErrorOnTemplateBody />
          )}
        </div>
        <Dialog
          header="Conditions are not met"
          headerClassName="text-red-500 gap-3"
          visible={errorDialogOpen}
          onHide={() => setErrorDialogOpen(false)}
          blockScroll
          className="text-xl"
        >
          {[...uniqueColumns]?.map(col => (
            <div key={col} className="mx-5">
              <h6>{`${col}:`}</h6>
              <ul>
                {signResponse?.validationErrors?.map(
                  err =>
                    err.attributeDisplayName === col && (
                      <li key={err?.message}>{err?.message || "Unknown validation message"}</li>
                    )
                )}
              </ul>
            </div>
          ))}
        </Dialog>
      </div>
    );
}

interface IError {
    text: string;
}

function Error(props: IError) {
    return (
        <div style={{ margin: 'auto', width: '50%', padding: '10px' }}>
            <div className='demo-container p-p-4' style={{ border: '2px solid #3C76DA', borderRadius: '25px' }}>
                <h3 className='p-d-block p-mx-auto' style={{ textAlign: 'center' }}>
                    {props.text}
                </h3>
            </div>
        </div>
    );
}
